import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, MapPin, Clock, Camera, User, CheckCircle, Navigation, Phone, AlertTriangle, BellRing, X } from 'lucide-react';
import { dispatchFetch, T, F, bd, fmtTime, fmtDate } from '@/lib/dispatchApi';
import BricksLoader from "@/components/BricksLoader";
import DispatchButton from "@/components/dispatch/DispatchButton";

const NOSHOW_GEOFENCE_METERS = 150;

function haversineMeters(lat1, lon1, lat2, lon2) {
  const R = 6371000;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2
    + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.asin(Math.sqrt(a));
}

function fmtJobType(t) {
  return t.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

const STATUS_COLOR = {
  pending: T.warnText, assigned: T.attnText, in_progress: T.attnText,
  completed: T.okText, cancelled: T.badText, agent_no_show: T.badText,
};

function InfoRow({ icon, label, children }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, paddingTop: 10, paddingBottom: 10 }}>
      <div style={{ width: 20, marginTop: 2 }}>{icon}</div>
      <div style={{ flex: 1 }}>
        <p style={{ fontSize: 13, fontWeight: 500, color: T.moonMute, margin: '0 0 4px 0' }}>{label}</p>
        {children}
      </div>
    </div>
  );
}

export default function DispatchJobDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actioning, setActioning] = useState(false);
  const [escalating, setEscalating] = useState(false);
  const [escalationSentAt, setEscalationSentAt] = useState(null);
  const [escalationWaitSecs, setEscalationWaitSecs] = useState(0);
  const [noShowVisible, setNoShowVisible] = useState(false);
  const [noShowNotes, setNoShowNotes] = useState('');
  const [submittingNoShow, setSubmittingNoShow] = useState(false);
  const [distanceMeters, setDistanceMeters] = useState(null);

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const res = await dispatchFetch(`/api/vendor-os/jobs/${id}`);
        if (!res.ok) throw new Error(`${res.status}`);
        setJob(await res.json());
      } catch {
        alert('Could not load job details.');
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  useEffect(() => {
    if (!job || !['assigned', 'in_progress'].includes(job.status)) return;
    if (job.lat == null || job.lng == null) return;
    navigator.geolocation?.getCurrentPosition(
      pos => setDistanceMeters(haversineMeters(pos.coords.latitude, pos.coords.longitude, job.lat, job.lng)),
      () => {},
      { enableHighAccuracy: true }
    );
  }, [job?.id, job?.status]);

  useEffect(() => {
    if (!escalationSentAt) return;
    const WAIT_MS = 15 * 60 * 1000;
    const tick = () => setEscalationWaitSecs(Math.ceil(Math.max(0, WAIT_MS - (Date.now() - escalationSentAt)) / 1000));
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [escalationSentAt]);

  const markComplete = async () => {
    if (!job || !window.confirm('Confirm that this shoot is finished?')) return;
    setActioning(true);
    try {
      const res = await dispatchFetch(`/api/vendor-os/jobs/${job.id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'completed' }),
      });
      if (!res.ok) throw new Error(`${res.status}`);
      setJob(prev => prev ? { ...prev, status: 'completed' } : prev);
    } catch {
      alert('Could not update job status.');
    } finally {
      setActioning(false);
    }
  };

  const openMaps = () => {
    if (!job) return;
    const addr = encodeURIComponent(`${job.address}, ${job.city}, ${job.state}`);
    window.open(`https://maps.google.com/?daddr=${addr}`, '_blank');
  };

  const notifyAgent = async () => {
    if (!job || escalating) return;
    setEscalating(true);
    try {
      const res = await dispatchFetch(`/api/vendor-os/jobs/${job.id}/escalate-absent-agent`, { method: 'POST' });
      if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || `HTTP ${res.status}`); }
      setEscalationSentAt(Date.now());
    } catch (e) {
      alert('Could not notify agent: ' + (e.message || 'Try again.'));
    } finally {
      setEscalating(false);
    }
  };

  const submitNoShow = async () => {
    if (!job || submittingNoShow) return;
    setSubmittingNoShow(true);
    try {
      let lat, lng;
      try {
        await new Promise((resolve) =>
          navigator.geolocation?.getCurrentPosition(
            pos => { lat = pos.coords.latitude; lng = pos.coords.longitude; resolve(); },
            resolve,
            { enableHighAccuracy: true }
          )
        );
      } catch {}

      const body = {};
      if (noShowNotes.trim()) body.notes = noShowNotes.trim();
      if (lat !== undefined) { body.photographer_lat = lat; body.photographer_lng = lng; }

      const res = await dispatchFetch(`/api/vendor-os/jobs/${job.id}/agent-no-show`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);

      setNoShowVisible(false);
      setNoShowNotes('');
      if (data.hold_for_review) {
        alert('Held for Review: Your claim has been flagged for location verification. Our team will review within 2 business days.');
      } else {
        alert('No-show filed. Bricks will review it.');
        setJob(prev => prev ? { ...prev, status: 'agent_no_show' } : prev);
      }
    } catch (e) {
      alert(e.message || 'Could not file claim. Try again.');
    } finally {
      setSubmittingNoShow(false);
    }
  };

  if (loading) {
    return (
      <div style={{ backgroundColor: T.night, height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <BricksLoader variant="assemble" size={40} color={T.brick} />
      </div>
    );
  }

  if (!job) {
    return (
      <div style={{ backgroundColor: T.night, height: '100vh', color: T.moon, fontFamily: F.heading }}>
        <button className="bx-tap" onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', padding: '14px 20px', cursor: 'pointer', display: 'flex' }}>
          <ArrowLeft size={20} color={T.moon} strokeWidth={1.5} />
        </button>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', paddingTop: 60 }}>
          <p style={{ color: T.moonMute, fontSize: 15 }}>Job not found.</p>
        </div>
      </div>
    );
  }

  const statusColor = STATUS_COLOR[job.status] || T.moonMute;
  const isComplete = job.status === 'completed';
  const isActive = ['assigned', 'in_progress'].includes(job.status);
  const canReportNoShow = isActive && (() => {
    if (job.lat != null && job.lng != null && distanceMeters !== null && distanceMeters > NOSHOW_GEOFENCE_METERS) return false;
    if (!job.scheduled_date || !job.time_window_start) return false;
    try {
      const time24 = (job.time_window_start || '').slice(0, 5);
      const shootMs = new Date(`${job.scheduled_date}T${time24}:00`).getTime();
      if (isNaN(shootMs)) return false;
      const now = Date.now();
      return now >= shootMs - 15 * 60 * 1000 && now <= shootMs + 90 * 60 * 1000;
    } catch { return false; }
  })();

  return (
    <div style={{ backgroundColor: T.night, height: '100dvh', color: T.moon, fontFamily: F.heading, display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 20px', borderBottom: `1px solid ${T.nightHair}`, flexShrink: 0 }}>
        <button className="bx-tap" onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', width: 36, height: 36, display: 'flex', alignItems: 'center', cursor: 'pointer', padding: 0 }}>
          <ArrowLeft size={20} color={T.moon} strokeWidth={1.5} />
        </button>
        <span style={{ flex: 1, fontSize: 15, fontWeight: 500, color: T.moon }}>Job</span>
        <span style={{ fontFamily: F.heading, fontSize: 13, color: statusColor }}>{(job.status || '').replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase())}</span>
      </div>

      {/* Scrollable content */}
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 20, paddingBottom: 24, display: 'flex', flexDirection: 'column', gap: 12 }}>
        {/* Hero */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 4 }}>
          <div style={{ width: 44, height: 44, borderRadius: 22, backgroundColor: T.nightSoft, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Camera size={22} color={T.brick} strokeWidth={1.5} />
          </div>
          <div style={{ flex: 1 }}>
            <p style={{ fontSize: 20, fontWeight: 500, color: T.moon, letterSpacing: -0.4, marginBottom: 2 }}>{fmtJobType(job.job_type)}</p>
            {job.package_name && <p style={{ fontSize: 13, color: T.moonMute, margin: 0 }}>{job.package_name}</p>}
          </div>
          {job.vendor_payout != null && (
            <span style={{ fontFamily: F.mono, fontSize: 22, color: T.moon }}>${job.vendor_payout.toFixed(0)}</span>
          )}
        </div>

        {/* Info card */}
        <div style={{ backgroundColor: T.nightSoft, borderRadius: 2, ...bd(T.nightHair), padding: 16 }}>
          <InfoRow icon={<MapPin size={16} color={T.moonMute} strokeWidth={1.5} />} label="Address">
            <p style={{ fontSize: 15, fontWeight: 500, color: T.moon, margin: '0 0 2px 0' }}>{job.address}</p>
            <p style={{ fontSize: 13, color: T.moonSoft, margin: 0 }}>{job.city}, {job.state}</p>
          </InfoRow>
          <div style={{ height: 1, backgroundColor: T.nightHair }} />
          <InfoRow icon={<Clock size={16} color={T.moonMute} strokeWidth={1.5} />} label="Scheduled">
            <p style={{ fontSize: 15, fontWeight: 500, color: T.moon, margin: '0 0 2px 0' }}>{fmtDate(job.scheduled_date)}</p>
            <p style={{ fontSize: 13, color: T.moonSoft, margin: 0 }}>{fmtTime(job.time_window_start)}</p>
          </InfoRow>
          {job.agent_name && (
            <>
              <div style={{ height: 1, backgroundColor: T.nightHair }} />
              <InfoRow icon={<User size={16} color={T.moonMute} strokeWidth={1.5} />} label="Agent">
                <p style={{ fontSize: 15, fontWeight: 500, color: T.moon, margin: 0 }}>{job.agent_name}</p>
              </InfoRow>
            </>
          )}
        </div>

        {/* Instructions */}
        {job.client_instructions && (
          <div style={{ backgroundColor: T.nightSoft, borderRadius: 2, ...bd(T.nightHair), padding: 16 }}>
            <p style={{ fontSize: 13, fontWeight: 500, color: T.moonMute, margin: '0 0 12px 0' }}>Instructions</p>
            <p style={{ fontSize: 14, color: T.moonSoft, lineHeight: 1.6, margin: 0 }}>{job.client_instructions}</p>
          </div>
        )}

        {/* Checklist */}
        {job.checklist?.length > 0 && (
          <div style={{ backgroundColor: T.nightSoft, borderRadius: 2, ...bd(T.nightHair), padding: 16 }}>
            <p style={{ fontSize: 13, fontWeight: 500, color: T.moonMute, margin: '0 0 12px 0' }}>Shoot checklist</p>
            {job.checklist.map(item => (
              <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 10, paddingTop: 7, paddingBottom: 7 }}>
                <CheckCircle size={16} color={item.completed ? T.okText : T.moonMute} strokeWidth={1.5} />
                <span style={{ fontSize: 14, color: item.completed ? T.moonFaint : T.moonSoft, flex: 1, textDecoration: item.completed ? 'line-through' : 'none' }}>
                  {item.label}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* No-show procedure */}
        {canReportNoShow && (
          <div style={{ backgroundColor: T.nightSoft, borderRadius: 2, ...bd(T.nightHairS), padding: 16 }}>
            <p style={{ fontSize: 13, fontWeight: 500, color: T.moonMute, margin: '0 0 12px 0' }}>No-show</p>
            {!escalationSentAt ? (
              <>
                <p style={{ fontSize: 13, color: T.moonSoft, lineHeight: 1.6, marginBottom: 14 }}>
                  Agent not here? Text them first. You can file a claim 15 min later.
                </p>
                <DispatchButton icon={BellRing} onClick={notifyAgent} loading={escalating}>Notify agent</DispatchButton>
              </>
            ) : escalationWaitSecs > 0 ? (
              <>
                <p style={{ fontSize: 13, color: T.moonSoft, marginBottom: 10 }}>Text sent. You can file a claim in:</p>
                <p style={{ fontFamily: F.mono, fontSize: 28, color: T.moon, letterSpacing: 2, textAlign: 'center', marginBottom: 4 }}>
                  {String(Math.floor(escalationWaitSecs / 60)).padStart(2, '0')}:{String(escalationWaitSecs % 60).padStart(2, '0')}
                </p>
              </>
            ) : (
              <>
                <p style={{ fontSize: 13, color: T.moonSoft, marginBottom: 14 }}>You can file a no-show claim now.</p>
                <DispatchButton variant="danger" icon={AlertTriangle} onClick={() => setNoShowVisible(true)}>Report no-show</DispatchButton>
              </>
            )}
          </div>
        )}
      </div>

      {/* Footer actions: equal-height secondaries, one primary (Mark complete) */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '12px 20px 34px', borderTop: `1px solid ${T.nightHair}`, backgroundColor: T.night, flexShrink: 0 }}>
        <div style={{ display: 'flex', gap: 10 }}>
          <DispatchButton icon={Navigation} onClick={openMaps} style={{ flex: 1 }}>Navigate</DispatchButton>
          {job.agent_phone && (
            <DispatchButton icon={Phone} href={`tel:${job.agent_phone}`} style={{ flex: 1 }}>Call agent</DispatchButton>
          )}
        </div>
        {!isComplete && (
          <DispatchButton variant="primary" icon={CheckCircle} onClick={markComplete} loading={actioning} full>Mark complete</DispatchButton>
        )}
      </div>

      {/* No-show modal */}
      {noShowVisible && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 50, display: 'flex', alignItems: 'flex-end' }}>
          <div style={{ backgroundColor: T.nightSoft, borderTopLeftRadius: 12, borderTopRightRadius: 12, padding: 'var(--bx-pad)', paddingBottom: 40, width: '100%', borderTop: `1px solid ${T.nightHairS}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <span style={{ fontSize: 15, fontWeight: 500, color: T.moon }}>Report no-show</span>
              <button className="bx-tap" onClick={() => { setNoShowVisible(false); setNoShowNotes(''); }} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
                <X size={18} color={T.moonMute} strokeWidth={1.5} />
              </button>
            </div>
            <p style={{ fontSize: 13, color: T.moonSoft, lineHeight: 1.6, marginBottom: 14 }}>
              Your location is recorded so Bricks can review the no-show.
            </p>
            <textarea
              value={noShowNotes}
              onChange={e => setNoShowNotes(e.target.value)}
              maxLength={500}
              placeholder="What happened? (optional)"
              style={{
                width: '100%', boxSizing: 'border-box', ...bd(T.nightHairS), borderRadius: 2,
                backgroundColor: T.night, color: T.moon, padding: 12, fontSize: 14, lineHeight: 1.6,
                minHeight: 100, marginBottom: 16, resize: 'vertical', fontFamily: F.heading,
                outline: 'none',
              }}
            />
            <DispatchButton variant="danger" onClick={submitNoShow} loading={submittingNoShow} full>Confirm no-show</DispatchButton>
          </div>
        </div>
      )}
    </div>
  );
}
